import { type ComponentProps, forwardRef, type MouseEvent, memo, useCallback } from 'react';
import DatePicker from 'react-datepicker';

import { DateInputField, type DateInputFieldProps } from './DateInputField';
import { DateInputHeader } from './DateInputHeader';

// react-datepicker's DatePickerProps includes internal props for subcomponents
// (like YearDropdown) that reuse names like 'onChange' with different signatures.
// This can cause TypeScript conflicts.
// To avoid this, destructure custom props before passing to DatePicker
// and explicitly type event handlers.
// Review if enabling multi-select or upgrading react-datepicker.
export type DateInputProps = {
  name?: string;
  onChange: (date: Date | [Date, Date] | null) => void;
  showClearButton?: boolean;
  showCalendarIcon?: boolean;
  testId?: string;
  selectsRange?: boolean;
} & Omit<
  Pick<
    ComponentProps<typeof DatePicker>,
    | 'dateFormat'
    | 'minDate'
    | 'maxDate'
    | 'showYearDropdown'
    | 'showMonthDropdown'
    | 'dropdownMode'
    | 'yearDropdownItemNumber'
    | 'scrollableYearDropdown'
    | 'disabled'
    | 'readOnly'
    | 'id'
    | 'selected'
    | 'startDate'
    | 'endDate'
    | 'includeDates'
    | 'excludeDates'
    | 'filterDate'
    | 'placeholderText'
    | 'todayButton'
    | 'locale'
    | 'showTimeSelect'
    | 'showTimeSelectOnly'
    | 'timeIntervals'
    | 'timeCaption'
    | 'injectTimes'
    | 'selectsEnd'
    | 'onSelect'
    | 'autoFocus'
    | 'onCalendarClose'
    | 'onBlur'
  >,
  'selectsRange'
> &
  Pick<DateInputFieldProps, 'name' | 'placeholder' | 'style' | 'className' | 'size'>;

export const DateInput = memo(
  forwardRef<HTMLInputElement, DateInputProps>(
    (
      {
        showCalendarIcon = true,
        onChange,
        name,
        size,
        showClearButton = true,
        style,
        className,
        placeholder,
        testId,
        selectsRange: _selectsRange,
        ...props
      },
      ref,
    ) => {
      const onDateChange = useCallback<DateInputProps['onChange']>(
        (date: Date | [Date, Date] | null) => {
          onChange(date);
        },
        [onChange],
      );

      const onClear = useCallback(
        (e: MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation();
          onDateChange(null); // Clear the selected date or range
        },
        [onDateChange],
      );

      const renderCustomHeader = useCallback<NonNullable<ComponentProps<typeof DatePicker>['renderCustomHeader']>>(
        (params) => <DateInputHeader {...params} />,
        [],
      );

      return (
        <DatePicker
          customInput={
            <DateInputField
              className={className}
              data-testid={testId}
              name={name}
              onClear={showClearButton ? onClear : undefined}
              placeholder={placeholder}
              ref={ref}
              showCalendarIcon={showCalendarIcon}
              size={size}
              style={style}
            />
          }
          onChange={onDateChange}
          popperPlacement="bottom-start"
          renderCustomHeader={renderCustomHeader}
          showPopperArrow={false}
          {...props}
        />
      );
    },
  ),
);

DateInput.displayName = 'DateInput';
